import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RequiredMark from '../../../shared/RequiredMark';
import DateInput from '../../../shared/DateInput';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { todayInTenant } from '../../../shared/tenantClock';
import '../../../shared/StyledInput.css';

const FIELD_LABEL_STYLE = { fontSize: 13, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// 'YYYY-MM-DD' `months` months after `ymd`, clamped to the end of a shorter
// month (31 Aug + 6 months = 28/29 Feb).
function addMonths(ymd, months) {
  const [y, m, d] = ymd.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

// Close a Prospect as Disqualified, Lost or a Custom closed Stage (issue
// #246) — shared by the Relationship card (its Close action and its Stage
// field), and the Pipeline's card menus. Asks for the closed Stage and a
// reason tagged for it; a reason with a revisit period also asks for a
// revisit date, pre-filled from that period, which the user can change or
// clear. initialStageId pre-selects the Stage (a Stage-field change).
// onClosed(entity) receives the updated record.
function CloseProspectDialog({ entity, initialStageId, onCancel, onClosed }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [closedStages, setClosedStages] = useState([]);
  const [stageId, setStageId] = useState(initialStageId ? String(initialStageId) : '');
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState('');
  const [revisitDate, setRevisitDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => {
        const prospectType = (res.data.data || []).find((t) => t.system_key === 'prospect');
        const stages = (prospectType?.stages || []).filter((s) => !s.is_open && s.is_active);
        setClosedStages(stages);
        if (!initialStageId && stages.length === 1) setStageId(String(stages[0].id));
      })
      .catch((err) => showError(err, 'Could not load closed Stages'));
    // eslint-disable-next-line
  }, []);

  // Only the reasons tagged for the chosen Stage are offered.
  useEffect(() => {
    setReasonId('');
    setRevisitDate('');
    if (!stageId) { setReasons([]); return; }
    axios.get('/api/close-reasons', { params: { status: 'active', stage_id: stageId } })
      .then((res) => setReasons(res.data.data || []))
      .catch((err) => showError(err, 'Could not load Close Reasons'));
    // eslint-disable-next-line
  }, [stageId]);

  const reason = reasons.find((r) => String(r.id) === reasonId);

  const pickReason = (id) => {
    setReasonId(id);
    const picked = reasons.find((r) => String(r.id) === id);
    setRevisitDate(picked?.revisit_months ? addMonths(todayInTenant(), picked.revisit_months) : '');
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`/api/commercial-entities/${entity.id}/close-prospect`, {
        stage_id: Number(stageId),
        close_reason_id: Number(reasonId),
        revisit_date: reason?.revisit_months ? (revisitDate || null) : null,
      });
      dismissError();
      onClosed(res.data.data);
    } catch (err) {
      showError(err, 'Close failed');
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title={`Close ${entity.name}`}
      description="It leaves the Pipeline and stays on the Prospects list. It can be reopened later."
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={save} loading={saving} disabled={!stageId || !reasonId}>
            {saving ? 'Closing...' : 'Close Prospect'}
          </SaveButton>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Close as<RequiredMark /></label><br />
          <select className="styled-input" value={stageId} onChange={(e) => setStageId(e.target.value)} style={FIELD_INPUT_STYLE}>
            <option value="">Select a Stage...</option>
            {closedStages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Reason<RequiredMark /></label><br />
          <select className="styled-input" value={reasonId} onChange={(e) => pickReason(e.target.value)} disabled={!stageId} style={FIELD_INPUT_STYLE}>
            <option value="">{stageId ? 'Select a reason...' : 'Pick a Stage first'}</option>
            {reasons.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {stageId && reasons.length === 0 && (
            <div style={{ fontSize: 12, color: '#b26a00', marginTop: 4 }}>
              No Close Reasons are offered for this Stage yet — add one under CRM &gt; Lists &gt; Close Reasons.
            </div>
          )}
        </div>
        {reason?.revisit_months && (
          <div>
            <label style={FIELD_LABEL_STYLE}>Revisit on</label><br />
            <DateInput value={revisitDate} onChange={(e) => setRevisitDate(e.target.value)} style={{ width: 180 }} />
            <div style={{ fontSize: 12, color: '#5a6b85', marginTop: 4 }}>
              Creates a follow-up Task for you on this date. Clear it to skip the revisit.
            </div>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}

export default CloseProspectDialog;
